import { Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  CreateVendorRequest,
  UpdateVendorRequest,
  VendorDiscoveryAdapter,
  VendorRecord,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCreateVendor,
  useDeleteVendor,
  useDiscoverVendorModels,
  useRegisterVendorDiscoveredModel,
  useSetVendorOfficialKey,
  useUpdateVendor,
  useVendorOfficialKey,
  useVendorsList,
} from '../api/useVendors.ts';
import { formatLocalDate } from '../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tooltip,
  Tr,
} from '../ui/index.ts';

type AdapterSelection = VendorDiscoveryAdapter | 'none';
type DiscoveryBlocker = 'adapter' | 'url' | 'key-loading' | 'key-error' | 'key-missing';
type VendorFormErrors = Partial<Record<keyof VendorFormState, string>>;

interface ManageConfigErrors {
  displayName?: string;
  modelDiscoveryUrl?: string;
}

interface VendorFormState {
  readonly slug: string;
  readonly displayName: string;
  readonly adapter: AdapterSelection;
  readonly modelDiscoveryUrl: string;
}

const EMPTY_VENDOR_FORM: VendorFormState = {
  slug: '',
  displayName: '',
  adapter: 'none',
  modelDiscoveryUrl: '',
};

const VENDOR_SLUG_PATTERN = /^[a-z][a-z0-9-]{0,62}$/;
const MAX_MODEL_DISCOVERY_URL_LENGTH = 2_048;
const RESERVED_DISCOVERY_HOST_SUFFIXES = [
  '.arpa',
  '.corp',
  '.example',
  '.home',
  '.internal',
  '.invalid',
  '.lan',
  '.local',
  '.localdomain',
  '.localhost',
  '.onion',
  '.test',
] as const;
const BUILTIN_DISCOVERY_HOSTS = [
  'api.anthropic.com',
  'api.openai.com',
  'api.x.ai',
  'generativelanguage.googleapis.com',
] as const;

function parseAdapterSelection(value: string): AdapterSelection {
  if (value === 'anthropic' || value === 'openai' || value === 'gemini') {
    return value;
  }
  return 'none';
}

function nullableAdapter(value: AdapterSelection): VendorDiscoveryAdapter | null {
  return value === 'none' ? null : value;
}

function nullableText(value: string): string | null {
  const normalized = value.trim();
  return normalized.length === 0 ? null : normalized;
}

function parseIpv4(hostname: string): readonly number[] | null {
  if (!/^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)) {
    return null;
  }
  const parts = hostname.split('.').map(Number);
  return parts.every((part) => Number.isInteger(part) && part >= 0 && part <= 255)
    ? parts
    : null;
}

function isReservedIpv4(parts: readonly number[]): boolean {
  const [a = 0, b = 0, c = 0] = parts;
  return (
    a === 0 ||
    a === 10 ||
    a === 127 ||
    (a === 100 && b >= 64 && b <= 127) ||
    (a === 169 && b === 254) ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 0 && c === 0) ||
    (a === 192 && b === 0 && c === 2) ||
    (a === 192 && b === 31 && c === 196) ||
    (a === 192 && b === 52 && c === 193) ||
    (a === 192 && b === 88 && c === 99) ||
    (a === 192 && b === 168) ||
    (a === 192 && b === 175 && c === 48) ||
    (a === 198 && (b === 18 || b === 19)) ||
    (a === 198 && b === 51 && c === 100) ||
    (a === 203 && b === 0 && c === 113) ||
    a >= 224
  );
}

function parseIpv6(rawHostname: string): readonly number[] | null {
  let hostname = rawHostname.toLowerCase();
  if (hostname.startsWith('[') && hostname.endsWith(']')) {
    hostname = hostname.slice(1, -1);
  }
  if (!hostname.includes(':') || hostname.includes('%')) {
    return null;
  }
  if (hostname.includes('.')) {
    const lastColon = hostname.lastIndexOf(':');
    const ipv4 = parseIpv4(hostname.slice(lastColon + 1));
    if (lastColon < 0 || ipv4 === null) {
      return null;
    }
    const high = ((ipv4[0] ?? 0) << 8) | (ipv4[1] ?? 0);
    const low = ((ipv4[2] ?? 0) << 8) | (ipv4[3] ?? 0);
    hostname = `${hostname.slice(0, lastColon)}:${high.toString(16)}:${low.toString(16)}`;
  }

  const halves = hostname.split('::');
  if (halves.length > 2) {
    return null;
  }
  const parseHalf = (half: string): number[] | null => {
    if (half.length === 0) return [];
    const values = half.split(':');
    if (!values.every((part) => /^[0-9a-f]{1,4}$/.test(part))) {
      return null;
    }
    return values.map((part) => Number.parseInt(part, 16));
  };
  const left = parseHalf(halves[0] ?? '');
  const right = parseHalf(halves[1] ?? '');
  if (left === null || right === null) {
    return null;
  }
  if (halves.length === 1) {
    return left.length === 8 ? left : null;
  }
  const zeroCount = 8 - left.length - right.length;
  return zeroCount >= 1
    ? [...left, ...Array.from({ length: zeroCount }, () => 0), ...right]
    : null;
}

function isReservedIpv6(parts: readonly number[]): boolean {
  const [a = 0, b = 0, c = 0, d = 0, e = 0, f = 0, g = 0, h = 0] = parts;
  return (
    parts.every((part) => part === 0) ||
    (a === 0 && b === 0 && c === 0 && d === 0 && e === 0 && f === 0 && g === 0 && h === 1) ||
    (a === 0 && b === 0 && c === 0 && d === 0 && e === 0) ||
    (a & 0xfe00) === 0xfc00 ||
    (a & 0xffc0) === 0xfe80 ||
    (a & 0xffc0) === 0xfec0 ||
    (a & 0xff00) === 0xff00 ||
    (a === 0x0064 && b === 0xff9b) ||
    (a === 0x0100 && b === 0 && c === 0 && d === 0) ||
    (a === 0x2001 && (b & 0xfe00) === 0) ||
    (a === 0x2001 && b === 0x0db8) ||
    (a === 0x2001 && (b & 0xfff0) === 0x0010) ||
    (a === 0x2001 && (b & 0xfff0) === 0x0020) ||
    a === 0x2002
  );
}

function isPublicDiscoveryHostname(rawHostname: string): boolean {
  const rawLowercaseHostname = rawHostname.toLowerCase();
  const hostname = rawLowercaseHostname.replace(/\.$/, '');
  if (hostname.length === 0 || hostname.length > 253) return false;
  const ipv4 = parseIpv4(hostname);
  if (ipv4 !== null) return !isReservedIpv4(ipv4);
  if (/^[\d.]+$/.test(hostname)) return false;
  const ipv6 = parseIpv6(hostname);
  if (ipv6 !== null) return !isReservedIpv6(ipv6);
  if (hostname.includes(':') || hostname === 'localhost' || !hostname.includes('.')) return false;
  if (RESERVED_DISCOVERY_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix))) return false;
  if (BUILTIN_DISCOVERY_HOSTS.some(
    (official) => hostname === official && rawLowercaseHostname !== official,
  )) return false;
  if (BUILTIN_DISCOVERY_HOSTS.some((official) => hostname.endsWith(`.${official}`))) return false;
  return hostname.split('.').every(
    (label) => label.length > 0 && label.length <= 63 && /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(label),
  );
}

function isValidDiscoveryUrl(value: string): boolean {
  if (value.length > MAX_MODEL_DISCOVERY_URL_LENGTH) {
    return false;
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:'
      && parsed.port.length === 0
      && parsed.username.length === 0
      && parsed.password.length === 0
      && parsed.hash.length === 0
      && parsed.search.length === 0
      && !value.includes('?')
      && isPublicDiscoveryHostname(parsed.hostname)
      && parsed.toString().length <= MAX_MODEL_DISCOVERY_URL_LENGTH;
  } catch {
    return false;
  }
}

function discoveryUrlServerError(
  t: ReturnType<typeof useT>,
  cause: unknown,
): string | null {
  if (!(cause instanceof OpsApiError) || cause.code !== 'INVALID_PAYLOAD') {
    return null;
  }
  switch (cause.message) {
    case 'model_discovery_url must be an HTTPS URL or null':
    case 'model_discovery_url must not be empty; use null to clear it':
    case 'model_discovery_url must be an absolute HTTPS URL':
      return t('vendors.validation.url_absolute');
    case 'model_discovery_url must use HTTPS':
      return t('vendors.validation.url_https');
    case 'model_discovery_url must use the default HTTPS port':
      return t('vendors.validation.url_port');
    case 'model_discovery_url must not include username or password':
      return t('vendors.validation.url_credentials');
    case 'model_discovery_url must not include a fragment':
      return t('vendors.validation.url_fragment');
    case 'model_discovery_url must not include query parameters; remove the query string and configure credentials separately':
      return t('vendors.validation.url_query');
    case 'model_discovery_url must use a public hostname or IP address':
      return t('vendors.validation.url_public');
    case 'model_discovery_url origin is not permitted by deployment policy':
      return t('vendors.validation.url_origin');
    case `model_discovery_url must be at most ${MAX_MODEL_DISCOVERY_URL_LENGTH} characters`:
      return t('vendors.validation.url_length');
    default:
      return null;
  }
}

function shouldRenderKeyPreview(preview: string | null): preview is string {
  return preview !== null && preview.trim().toLowerCase() !== 'configured';
}

function focusField(fieldId: string): void {
  queueMicrotask(() => document.getElementById(fieldId)?.focus());
}

function hasDiscoveryAdapter(adapter: VendorDiscoveryAdapter | null): adapter is VendorDiscoveryAdapter {
  return adapter === 'anthropic' || adapter === 'openai' || adapter === 'gemini';
}

function hasDiscoveryUrl(url: string | null): url is string {
  return url !== null && url.trim().length > 0;
}

function adapterLabel(
  t: ReturnType<typeof useT>,
  adapter: VendorDiscoveryAdapter | null,
): string {
  switch (adapter) {
    case 'anthropic':
      return t('vendors.adapter.anthropic');
    case 'openai':
      return t('vendors.adapter.openai');
    case 'gemini':
      return t('vendors.adapter.gemini');
    default:
      return t('vendors.adapter.none');
  }
}

function discoveryBlockerLabel(
  t: ReturnType<typeof useT>,
  blocker: DiscoveryBlocker,
): string {
  switch (blocker) {
    case 'adapter':
      return t('vendors.discovery.blocker.adapter');
    case 'url':
      return t('vendors.discovery.blocker.url');
    case 'key-loading':
      return t('vendors.discovery.blocker.key_loading');
    case 'key-error':
      return t('vendors.discovery.blocker.key_error');
    case 'key-missing':
      return t('vendors.discovery.blocker.key_missing');
  }
}

function discoveryBlockers(
  vendor: VendorRecord,
  key: ReturnType<typeof useVendorOfficialKey>,
): readonly DiscoveryBlocker[] {
  const blockers: DiscoveryBlocker[] = [];
  if (!hasDiscoveryAdapter(vendor.discovery_adapter)) {
    blockers.push('adapter');
  }
  if (!hasDiscoveryUrl(vendor.model_discovery_url)) {
    blockers.push('url');
  }
  if (key.isLoading) {
    blockers.push('key-loading');
  } else if (key.isError) {
    blockers.push('key-error');
  } else if (key.data?.credential.has_key !== true) {
    blockers.push('key-missing');
  }
  return blockers;
}

function discoveryAdapterOptions(t: ReturnType<typeof useT>) {
  return [
    { value: 'none', label: t('vendors.adapter.none') },
    { value: 'anthropic', label: t('vendors.adapter.anthropic') },
    { value: 'openai', label: t('vendors.adapter.openai') },
    { value: 'gemini', label: t('vendors.adapter.gemini') },
  ] as const;
}

function discoveryFailureMessage(
  t: ReturnType<typeof useT>,
  cause: unknown,
): string {
  if (!(cause instanceof OpsApiError)) {
    return t('vendors.discovery.error.unavailable');
  }
  switch (cause.code) {
    case 'INVALID_CREDENTIALS':
      return t('vendors.discovery.error.credentials');
    case 'RATE_LIMITED':
      return t('vendors.discovery.error.rate_limited');
    case 'VENDOR_OFFICIAL_KEY_NOT_FOUND':
      return t('vendors.discovery.error.key_binding');
    case 'INVALID_PAYLOAD':
      return t('vendors.discovery.error.configuration');
    default:
      return t('vendors.discovery.error.unavailable');
  }
}

function deleteFailureMessage(
  t: ReturnType<typeof useT>,
  cause: unknown,
): string {
  if (cause instanceof OpsApiError && cause.code === 'VENDOR_DEPENDENCIES_EXIST') {
    return t('vendors.delete_modal.dependencies_error');
  }
  if (cause instanceof OpsApiError && cause.status === 409) {
    return t('vendors.delete_modal.busy_error');
  }
  return coerceErrorMessage(cause);
}

export function VendorsPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { data: vendors, isLoading, error, refetch, isFetching } = useVendorsList();
  const createMut = useCreateVendor();
  const deleteMut = useDeleteVendor();

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<VendorFormState>(EMPTY_VENDOR_FORM);
  const [createErrors, setCreateErrors] = useState<VendorFormErrors>({});
  const [manageTarget, setManageTarget] = useState<VendorRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VendorRecord | null>(null);

  const filtered = useMemo(() => {
    if (!vendors) return [];
    const query = search.trim().toLowerCase();
    if (query.length === 0) return vendors;
    return vendors.filter(
      (vendor) =>
        vendor.slug.toLowerCase().includes(query) ||
        vendor.display_name.toLowerCase().includes(query),
    );
  }, [vendors, search]);

  function closeCreate(): void {
    setCreateOpen(false);
    setCreateForm(EMPTY_VENDOR_FORM);
    setCreateErrors({});
    createMut.reset();
  }

  function confirmDelete(): void {
    if (deleteTarget === null) {
      return;
    }
    deleteMut.mutate(deleteTarget.slug, {
      onSuccess: () => {
        toast.success(t('vendors.toast.deleted'));
        setDeleteTarget(null);
      },
      onError: (cause) => {
        toast.error(deleteFailureMessage(t, cause));
      },
    });
  }

  function handleCreate(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const slug = createForm.slug.trim();
    const displayName = createForm.displayName.trim();
    const modelDiscoveryUrl = nullableText(createForm.modelDiscoveryUrl);
    const adapter = nullableAdapter(createForm.adapter);
    const errors: VendorFormErrors = {};

    if (slug.length === 0) {
      errors.slug = t('vendors.validation.required');
    } else if (!VENDOR_SLUG_PATTERN.test(slug)) {
      errors.slug = t('vendors.validation.slug');
    }
    if (displayName.length === 0) {
      errors.displayName = t('vendors.validation.required');
    }
    if (modelDiscoveryUrl !== null && !isValidDiscoveryUrl(modelDiscoveryUrl)) {
      errors.modelDiscoveryUrl = t('vendors.validation.url');
    }
    if ((adapter === null) !== (modelDiscoveryUrl === null)) {
      errors.adapter = t('vendors.validation.discovery_pair');
      errors.modelDiscoveryUrl = t('vendors.validation.discovery_pair');
    }

    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      const firstInvalidId = errors.slug !== undefined
        ? 'vendors-create-slug'
        : errors.displayName !== undefined
          ? 'vendors-create-display-name'
          : errors.adapter !== undefined
            ? 'vendors-create-discovery-adapter'
            : 'vendors-create-model-discovery-url';
      focusField(firstInvalidId);
      return;
    }

    setCreateErrors({});
    const body: CreateVendorRequest = {
      slug,
      display_name: displayName,
      discovery_adapter: adapter,
      model_discovery_url: modelDiscoveryUrl,
    };
    createMut.mutate(body, {
      onSuccess: () => {
        toast.success(t('vendors.toast.created'));
        closeCreate();
      },
      onError: (cause) => {
        const urlError = discoveryUrlServerError(t, cause);
        if (urlError !== null) {
          setCreateErrors((current) => ({ ...current, modelDiscoveryUrl: urlError }));
          focusField('vendors-create-model-discovery-url');
          toast.error(urlError);
        } else if (cause instanceof OpsApiError && cause.code === 'VENDOR_ALREADY_EXISTS') {
          toast.error(t('vendors.toast.duplicate'));
        } else {
          toast.error(t('vendors.create_modal.config_error'));
        }
      },
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('vendors.title')}
        sub={t('vendors.subtitle')}
        actions={
          <Button
            variant="primary"
            onClick={() => setCreateOpen(true)}
            data-testid="vendors-create-btn"
            disabled={isLoading || error !== null}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            {t('vendors.create_btn')}
          </Button>
        }
      />

      {isLoading ? (
        <div data-testid="vendors-loading" className="flex flex-col gap-4" aria-label={t('vendors.loading')}>
          {[1, 2, 3].map((item) => (
            <Skeleton key={item} className="h-12 w-full" />
          ))}
        </div>
      ) : error !== null ? (
        <div
          data-testid="vendors-error"
          className="flex flex-col items-start gap-3 rounded-lg border border-bad-line bg-bad-dim px-4 py-3"
          role="alert"
        >
          <div className="flex flex-col gap-1">
            <p className="m-0 text-body font-medium text-bad-bright">{t('vendors.error.title')}</p>
            <p className="m-0 text-dense text-bad-bright">{t('vendors.error.hint')}</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { void refetch(); }}
            loading={isFetching}
            data-testid="vendors-retry"
          >
            {isFetching ? t('vendors.error.retrying') : t('vendors.error.retry')}
          </Button>
        </div>
      ) : (
        <>
          <div className="relative max-w-md">
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('vendors.search.placeholder')}
              aria-label={t('vendors.search.aria_label')}
              data-testid="vendors-search"
              className="pr-9"
            />
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-paper-low"
            />
          </div>

          {vendors?.length === 0 ? (
            <EmptyState
              testId="vendors-empty"
              title={t('vendors.empty.title')}
              hint={t('vendors.empty.hint')}
              action={(
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  <Plus aria-hidden="true" className="h-4 w-4" />
                  {t('vendors.empty.action')}
                </Button>
              )}
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              testId="vendors-search-empty"
              title={t('vendors.search_empty.title')}
              hint={t('vendors.search_empty.hint', { query: search.trim() })}
              action={(
                <Button variant="secondary" onClick={() => setSearch('')} data-testid="vendors-clear-search">
                  {t('vendors.search_empty.clear')}
                </Button>
              )}
            />
          ) : (
            <div className="flex flex-col gap-2">
              <p data-testid="vendors-table-scroll-hint" className="m-0 text-label text-paper-low lg:hidden">
                {t('vendors.table.scroll_hint')}
              </p>
              <Table
                testId="vendors-table"
                className="[&>table]:min-w-[940px]"
              >
                <THead>
                  <Tr interactive={false}>
                    <Th>{t('vendors.col.slug')}</Th>
                    <Th>{t('vendors.col.display_name')}</Th>
                    <Th>{t('vendors.col.discovery_config')}</Th>
                    <Th>{t('vendors.col.key_readiness')}</Th>
                    <Th>{t('vendors.col.created_at')}</Th>
                    <Th className="text-right">{t('vendors.col.actions')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {filtered.map((vendor) => (
                    <VendorRow
                      key={vendor.slug}
                      vendor={vendor}
                      deletePending={deleteMut.isPending}
                      onManage={setManageTarget}
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!open && !createMut.isPending) closeCreate();
        }}
        title={t('vendors.create_modal.title')}
        description={t('vendors.create_modal.description')}
        closeLabel={t('common.close')}
        testId="vendors-create-dialog"
      >
        <form onSubmit={handleCreate} noValidate className="flex flex-col gap-4">
          <FormField
            label={t('vendors.create_modal.slug')}
            fieldId="vendors-create-slug"
            required
            error={createErrors.slug}
          >
            <Input
              id="vendors-create-slug"
              name="slug"
              mono
              value={createForm.slug}
              onChange={(event) => {
                setCreateForm((current) => ({ ...current, slug: event.target.value }));
                setCreateErrors((current) => ({ ...current, slug: undefined }));
              }}
              placeholder={t('vendors.create_modal.slug_placeholder')}
              data-testid="vendors-create-slug"
              disabled={createMut.isPending}
              required
              invalid={createErrors.slug !== undefined}
              aria-describedby={createErrors.slug !== undefined ? 'vendors-create-slug-error' : undefined}
            />
          </FormField>
          <FormField
            label={t('vendors.create_modal.display_name')}
            fieldId="vendors-create-display-name"
            required
            error={createErrors.displayName}
          >
            <Input
              id="vendors-create-display-name"
              name="display_name"
              value={createForm.displayName}
              onChange={(event) => {
                setCreateForm((current) => ({ ...current, displayName: event.target.value }));
                setCreateErrors((current) => ({ ...current, displayName: undefined }));
              }}
              placeholder={t('vendors.create_modal.display_name_placeholder')}
              data-testid="vendors-create-display-name"
              disabled={createMut.isPending}
              required
              invalid={createErrors.displayName !== undefined}
              aria-describedby={createErrors.displayName !== undefined ? 'vendors-create-display-name-error' : undefined}
            />
          </FormField>
          <FormField
            label={t('vendors.create_modal.discovery_adapter')}
            fieldId="vendors-create-discovery-adapter"
            hint={t('vendors.create_modal.discovery_adapter_hint')}
            error={createErrors.adapter}
          >
            <Select
              id="vendors-create-discovery-adapter"
              value={createForm.adapter}
              onValueChange={(value) => {
                setCreateForm((current) => ({ ...current, adapter: parseAdapterSelection(value) }));
                setCreateErrors((current) => ({ ...current, adapter: undefined, modelDiscoveryUrl: undefined }));
              }}
              options={discoveryAdapterOptions(t)}
              ariaLabel={t('vendors.create_modal.discovery_adapter')}
              testId="vendors-create-discovery-adapter"
              disabled={createMut.isPending}
              invalid={createErrors.adapter !== undefined}
              ariaDescribedBy={createErrors.adapter !== undefined ? 'vendors-create-discovery-adapter-error' : undefined}
            />
          </FormField>
          <FormField
            label={t('vendors.create_modal.model_discovery_url')}
            fieldId="vendors-create-model-discovery-url"
            hint={t('vendors.create_modal.model_discovery_url_hint')}
            error={createErrors.modelDiscoveryUrl}
          >
            <Input
              id="vendors-create-model-discovery-url"
              name="model_discovery_url"
              type="url"
              value={createForm.modelDiscoveryUrl}
              onChange={(event) => {
                setCreateForm((current) => ({ ...current, modelDiscoveryUrl: event.target.value }));
                setCreateErrors((current) => ({ ...current, adapter: undefined, modelDiscoveryUrl: undefined }));
              }}
              placeholder={t('vendors.create_modal.model_discovery_url_placeholder')}
              data-testid="vendors-create-model-discovery-url"
              disabled={createMut.isPending}
              invalid={createErrors.modelDiscoveryUrl !== undefined}
              aria-describedby={createErrors.modelDiscoveryUrl !== undefined ? 'vendors-create-model-discovery-url-error' : undefined}
            />
          </FormField>

          {createMut.isError ? (
            <div
              className="rounded border border-bad-line bg-bad-dim px-3 py-2 text-dense text-bad-bright"
              data-testid="vendors-create-error"
              role="alert"
            >
              {t('vendors.create_modal.config_error')}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={closeCreate} disabled={createMut.isPending}>
              {t('vendors.create_modal.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createMut.isPending}
              data-testid="vendors-create-submit"
            >
              {createMut.isPending
                ? t('vendors.create_modal.creating')
                : t('vendors.create_modal.submit')}
            </Button>
          </div>
        </form>
      </Dialog>

      {manageTarget !== null ? (
        <ManageVendorDialog vendor={manageTarget} onClose={() => setManageTarget(null)} />
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleteMut.isPending) {
            setDeleteTarget(null);
          }
        }}
        title={t('vendors.delete_modal.title')}
        body={t('vendors.delete_modal.body', { name: deleteTarget?.display_name ?? '' })}
        confirmLabel={deleteMut.isPending ? t('common.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={confirmDelete}
        tone="danger"
        loading={deleteMut.isPending}
        testId="vendors-delete-confirm"
      />
    </div>
  );
}

function VendorRow({
  vendor,
  deletePending,
  onManage,
  onDelete,
}: {
  readonly vendor: VendorRecord;
  readonly deletePending: boolean;
  readonly onManage: (vendor: VendorRecord) => void;
  readonly onDelete: (vendor: VendorRecord) => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const key = useVendorOfficialKey(vendor.slug);
  const discover = useDiscoverVendorModels();
  const blockers = discoveryBlockers(vendor, key);
  const discoverDisabled = blockers.length > 0 || discover.isPending;
  const blockerSummary = blockers.map((blocker) => discoveryBlockerLabel(t, blocker)).join(', ');
  const discoverLabel = discover.isPending
    ? t('vendors.row.discovering')
    : blockerSummary.length > 0
      ? `${t('vendors.row.discover')}: ${blockerSummary}`
      : t('vendors.row.discover');

  function runDiscovery(): void {
    discover.mutate(vendor.slug, {
      onSuccess: (result) => {
        toast.success(t('vendors.toast.discovered', { seen: result.seen, missing: result.missing }));
      },
      onError: (cause) => {
        toast.error(discoveryFailureMessage(t, cause));
      },
    });
  }

  return (
    <Tr data-testid={`vendor-row-${vendor.slug}`}>
      <Td mono>{vendor.slug}</Td>
      <Td emphasis>{vendor.display_name}</Td>
      <Td>
        <div className="flex min-w-52 flex-col gap-1">
          <Chip tone="neutral" className="self-start">
            {adapterLabel(t, vendor.discovery_adapter)}
          </Chip>
          {!hasDiscoveryUrl(vendor.model_discovery_url) ? (
            <span className="text-micro text-paper-low">
              {t('vendors.discovery.url_not_configured')}
            </span>
          ) : (
            <Tooltip content={<span className="font-mono">{vendor.model_discovery_url}</span>}>
              <span
                className="block w-52 truncate rounded-sm font-mono text-micro text-paper-mid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 xl:w-80"
                tabIndex={0}
              >
                {vendor.model_discovery_url}
              </span>
            </Tooltip>
          )}
        </div>
      </Td>
      <Td>
        <VendorKeyReadiness keyState={key} />
      </Td>
      <Td>{formatLocalDate(vendor.created_at)}</Td>
      <Td className="whitespace-nowrap text-right">
        <div className="flex flex-nowrap items-center justify-end gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onManage(vendor)}
            data-testid={`vendor-row-${vendor.slug}-manage`}
          >
            {t('vendors.row.manage')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            loading={discover.isPending}
            disabled={discoverDisabled}
            onClick={runDiscovery}
            data-testid={`vendor-row-${vendor.slug}-discover`}
            aria-label={discoverLabel}
            title={blockerSummary.length > 0 ? blockerSummary : undefined}
          >
            {discover.isPending ? t('vendors.row.discovering') : t('vendors.row.discover')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => onDelete(vendor)}
            disabled={deletePending}
            data-testid={`vendor-row-${vendor.slug}-delete`}
          >
            {t('vendors.row.delete')}
          </Button>
        </div>
      </Td>
    </Tr>
  );
}

function VendorKeyReadiness({
  keyState,
}: {
  readonly keyState: ReturnType<typeof useVendorOfficialKey>;
}): JSX.Element {
  const t = useT();
  if (keyState.isLoading) {
    return <Chip tone="neutral">{t('vendors.key.status.loading')}</Chip>;
  }
  if (keyState.isError) {
    return <Chip tone="bad">{t('vendors.key.status.error')}</Chip>;
  }

  const credential = keyState.data?.credential;
  if (credential?.has_key !== true) {
    return <Chip tone="warn">{t('vendors.key.status.missing')}</Chip>;
  }

  return (
    <div className="flex flex-col gap-1">
      <Chip tone="ok">{t('vendors.key.status.configured')}</Chip>
      {shouldRenderKeyPreview(credential.key_preview) ? (
        <span className="font-mono text-micro text-paper-mid">{credential.key_preview}</span>
      ) : null}
    </div>
  );
}

function ManageVendorDialog({
  vendor,
  onClose,
}: {
  readonly vendor: VendorRecord;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const key = useVendorOfficialKey(vendor.slug);
  const updateMut = useUpdateVendor();
  const setKeyMut = useSetVendorOfficialKey();
  const [displayName, setDisplayName] = useState(vendor.display_name);
  const [modelDiscoveryUrl, setModelDiscoveryUrl] = useState(vendor.model_discovery_url ?? '');
  const [savedDisplayName, setSavedDisplayName] = useState(vendor.display_name);
  const [savedModelDiscoveryUrl, setSavedModelDiscoveryUrl] = useState(vendor.model_discovery_url);
  const [officialKey, setOfficialKey] = useState('');
  const [configErrors, setConfigErrors] = useState<ManageConfigErrors>({});
  const [officialKeyError, setOfficialKeyError] = useState<string | undefined>();
  const registerModelMut = useRegisterVendorDiscoveredModel();
  const [manualModel, setManualModel] = useState('');
  const [manualDisplayName, setManualDisplayName] = useState('');
  const [manualReason, setManualReason] = useState('');
  const [manualError, setManualError] = useState<string | undefined>();
  const pending = updateMut.isPending || setKeyMut.isPending || registerModelMut.isPending;
  const normalizedDisplayName = displayName.trim();
  const normalizedModelDiscoveryUrl = nullableText(modelDiscoveryUrl);
  const configDirty = normalizedDisplayName !== savedDisplayName
    || normalizedModelDiscoveryUrl !== savedModelDiscoveryUrl;
  const urlChanged = normalizedModelDiscoveryUrl !== savedModelDiscoveryUrl;

  function handleConfigSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (updateMut.isPending) {
      return;
    }

    const errors: ManageConfigErrors = {};
    if (normalizedDisplayName.length === 0) {
      errors.displayName = t('vendors.validation.required');
    }
    if (normalizedModelDiscoveryUrl !== null && !isValidDiscoveryUrl(normalizedModelDiscoveryUrl)) {
      errors.modelDiscoveryUrl = t('vendors.validation.url');
    }
    if (Object.keys(errors).length > 0) {
      setConfigErrors(errors);
      focusField(errors.displayName !== undefined
        ? 'vendors-manage-display-name'
        : 'vendors-manage-model-discovery-url');
      return;
    }

    setConfigErrors({});
    updateMut.reset();
    const body: UpdateVendorRequest = {
      display_name: normalizedDisplayName,
      model_discovery_url: normalizedModelDiscoveryUrl,
    };

    void (async () => {
      try {
        const result = await updateMut.mutateAsync({ slug: vendor.slug, body });
        setDisplayName(result.vendor.display_name);
        setModelDiscoveryUrl(result.vendor.model_discovery_url ?? '');
        setSavedDisplayName(result.vendor.display_name);
        setSavedModelDiscoveryUrl(result.vendor.model_discovery_url);
        await key.refetch();
        toast.success(t('vendors.toast.updated'));
      } catch (cause) {
        const urlError = discoveryUrlServerError(t, cause);
        if (urlError !== null) {
          setConfigErrors((current) => ({ ...current, modelDiscoveryUrl: urlError }));
          focusField('vendors-manage-model-discovery-url');
          toast.error(urlError);
        } else {
          toast.error(t('vendors.manage_modal.config_error'));
        }
      }
    })();
  }

  function handleKeySubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (setKeyMut.isPending) {
      return;
    }
    if (configDirty) {
      setOfficialKeyError(t('vendors.manage_modal.unsaved_config'));
      return;
    }

    const rotatedKey = officialKey.trim();
    if (rotatedKey.length === 0) {
      setOfficialKeyError(t('vendors.validation.required'));
      focusField('vendors-manage-official-key');
      return;
    }

    setOfficialKeyError(undefined);
    setKeyMut.reset();
    void (async () => {
      try {
        await setKeyMut.mutateAsync({ slug: vendor.slug, api_key: rotatedKey });
        setOfficialKey('');
        await key.refetch();
        toast.success(t('vendors.toast.key_rotated'));
      } catch {
        toast.error(t('vendors.manage_modal.key_error'));
      }
    })();
  }

  function handleManualRegisterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (registerModelMut.isPending) {
      return;
    }
    const model = manualModel.trim();
    const reason = manualReason.trim();
    if (model.length === 0 || reason.length === 0) {
      setManualError(t('vendors.validation.required'));
      focusField(model.length === 0 ? 'vendors-manage-manual-model' : 'vendors-manage-manual-reason');
      return;
    }
    setManualError(undefined);
    registerModelMut.reset();
    const displayName = nullableText(manualDisplayName);
    void (async () => {
      try {
        await registerModelMut.mutateAsync({
          slug: vendor.slug,
          body: { model, reason, ...(displayName === null ? {} : { display_name: displayName }) },
        });
        setManualModel('');
        setManualDisplayName('');
        setManualReason('');
        toast.success(t('vendors.toast.model_registered', { model }));
      } catch (cause) {
        const message = coerceErrorMessage(cause, t('vendors.manage_modal.manual_error'));
        setManualError(message);
        toast.error(message);
      }
    })();
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !pending) onClose();
      }}
      title={t('vendors.manage_modal.title', { name: vendor.display_name })}
      description={t('vendors.manage_modal.description')}
      closeLabel={t('common.close')}
      testId="vendors-manage-dialog"
      size="lg"
    >
      <div className="flex flex-col gap-5">
        <FormField
          label={t('vendors.manage_modal.slug')}
          fieldId="vendors-manage-slug"
          hint={t('vendors.manage_modal.slug_hint')}
        >
          <Input
            id="vendors-manage-slug"
            value={vendor.slug}
            readOnly
            mono
            data-testid="vendors-manage-slug"
          />
        </FormField>
        <div className="flex flex-col gap-1.5" data-testid="vendors-manage-discovery-adapter">
          <span className="text-label text-paper-mid">
            {t('vendors.manage_modal.discovery_adapter')}
          </span>
          <div>
            <Chip tone="neutral">{adapterLabel(t, vendor.discovery_adapter)}</Chip>
          </div>
          <p className="m-0 text-label text-paper-low">
            {t('vendors.manage_modal.discovery_adapter_hint')}
          </p>
        </div>

        <form
          onSubmit={handleConfigSubmit}
          noValidate
          className="flex flex-col gap-4 rounded-lg border border-line-soft bg-card p-4"
          data-testid="vendors-manage-config-form"
        >
          <div className="flex flex-col gap-1">
            <h3 className="m-0 text-section text-paper">{t('vendors.manage_modal.config_section')}</h3>
            <p className="m-0 text-dense text-paper-low">{t('vendors.manage_modal.config_section_hint')}</p>
          </div>
          <FormField
            label={t('vendors.manage_modal.display_name')}
            fieldId="vendors-manage-display-name"
            required
            error={configErrors.displayName}
          >
            <Input
              id="vendors-manage-display-name"
              value={displayName}
              onChange={(event) => {
                setDisplayName(event.target.value);
                setConfigErrors((current) => ({ ...current, displayName: undefined }));
                updateMut.reset();
              }}
              data-testid="vendors-manage-display-name"
              disabled={updateMut.isPending}
              required
              invalid={configErrors.displayName !== undefined}
              aria-describedby={configErrors.displayName !== undefined ? 'vendors-manage-display-name-error' : undefined}
            />
          </FormField>
          <FormField
            label={t('vendors.manage_modal.model_discovery_url')}
            fieldId="vendors-manage-model-discovery-url"
            hint={t('vendors.manage_modal.model_discovery_url_hint')}
            error={configErrors.modelDiscoveryUrl}
          >
            <Input
              id="vendors-manage-model-discovery-url"
              type="url"
              value={modelDiscoveryUrl}
              onChange={(event) => {
                setModelDiscoveryUrl(event.target.value);
                setConfigErrors((current) => ({ ...current, modelDiscoveryUrl: undefined }));
                updateMut.reset();
              }}
              data-testid="vendors-manage-model-discovery-url"
              disabled={updateMut.isPending}
              invalid={configErrors.modelDiscoveryUrl !== undefined}
              aria-describedby={configErrors.modelDiscoveryUrl !== undefined ? 'vendors-manage-model-discovery-url-error' : undefined}
            />
          </FormField>

          {urlChanged ? (
            <p
              className="m-0 rounded border border-warn-line bg-warn-dim px-3 py-2 text-dense text-warn"
              data-testid="vendors-manage-url-warning"
              role="status"
            >
              {t('vendors.manage_modal.url_change_warning')}
            </p>
          ) : null}

          {updateMut.isError ? (
            <p
              className="m-0 rounded border border-bad-line bg-bad-dim px-3 py-2 text-dense text-bad-bright"
              data-testid="vendors-manage-config-error"
              role="alert"
            >
              {t('vendors.manage_modal.config_error')}
            </p>
          ) : null}

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              loading={updateMut.isPending}
              disabled={!configDirty || setKeyMut.isPending}
              data-testid="vendors-manage-config-submit"
            >
              {updateMut.isPending
                ? t('vendors.manage_modal.config_saving')
                : t('vendors.manage_modal.config_submit')}
            </Button>
          </div>
        </form>

        <form
          onSubmit={handleKeySubmit}
          noValidate
          className="flex flex-col gap-4 rounded-lg border border-line-soft bg-card p-4"
          data-testid="vendors-manage-key-form"
        >
          <div className="flex flex-col gap-1">
            <h3 className="m-0 text-section text-paper">{t('vendors.manage_modal.key_section')}</h3>
            <p className="m-0 text-dense text-paper-low">{t('vendors.manage_modal.key_section_hint')}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-label text-paper-mid">{t('vendors.manage_modal.key_status')}</span>
            <VendorKeyReadiness keyState={key} />
          </div>
          <FormField
            label={t('vendors.manage_modal.official_key')}
            fieldId="vendors-manage-official-key"
            hint={t('vendors.manage_modal.official_key_hint')}
            error={officialKeyError}
            required
          >
            <Input
              id="vendors-manage-official-key"
              name="api_key"
              type="password"
              autoComplete="new-password"
              mono
              value={officialKey}
              onChange={(event) => {
                setOfficialKey(event.target.value);
                setOfficialKeyError(undefined);
                setKeyMut.reset();
              }}
              placeholder={t('vendors.manage_modal.official_key_placeholder')}
              data-testid="vendors-manage-official-key"
              disabled={setKeyMut.isPending}
              required
              invalid={officialKeyError !== undefined}
              aria-describedby={officialKeyError !== undefined ? 'vendors-manage-official-key-error' : undefined}
            />
          </FormField>

          {configDirty ? (
            <p
              className="m-0 rounded border border-warn-line bg-warn-dim px-3 py-2 text-dense text-warn"
              data-testid="vendors-manage-unsaved-config"
              role="status"
            >
              {t('vendors.manage_modal.unsaved_config')}
            </p>
          ) : null}

          {setKeyMut.isError ? (
            <p
              className="m-0 rounded border border-bad-line bg-bad-dim px-3 py-2 text-dense text-bad-bright"
              data-testid="vendors-manage-key-error"
              role="alert"
            >
              {t('vendors.manage_modal.key_error')}
            </p>
          ) : null}

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="secondary"
              loading={setKeyMut.isPending}
              disabled={configDirty || updateMut.isPending}
              data-testid="vendors-manage-key-submit"
            >
              {setKeyMut.isPending
                ? t('vendors.manage_modal.key_rotating')
                : t('vendors.manage_modal.key_submit')}
            </Button>
          </div>
        </form>

        <form
          onSubmit={handleManualRegisterSubmit}
          noValidate
          className="flex flex-col gap-4 rounded-lg border border-line-soft bg-card p-4"
          data-testid="vendors-manage-manual-form"
        >
          <div className="flex flex-col gap-1">
            <h3 className="m-0 text-section text-paper">{t('vendors.manage_modal.manual_section')}</h3>
            <p className="m-0 text-dense text-paper-low">{t('vendors.manage_modal.manual_section_hint')}</p>
          </div>
          <FormField
            label={t('vendors.manage_modal.manual_model')}
            fieldId="vendors-manage-manual-model"
            required
          >
            <Input
              id="vendors-manage-manual-model"
              mono
              value={manualModel}
              onChange={(event) => {
                setManualModel(event.target.value);
                setManualError(undefined);
              }}
              placeholder={t('vendors.manage_modal.manual_model_placeholder')}
              data-testid="vendors-manage-manual-model"
              disabled={registerModelMut.isPending}
              required
            />
          </FormField>
          <FormField
            label={t('vendors.manage_modal.manual_display_name')}
            fieldId="vendors-manage-manual-display-name"
          >
            <Input
              id="vendors-manage-manual-display-name"
              value={manualDisplayName}
              onChange={(event) => {
                setManualDisplayName(event.target.value);
                setManualError(undefined);
              }}
              data-testid="vendors-manage-manual-display-name"
              disabled={registerModelMut.isPending}
            />
          </FormField>
          <FormField
            label={t('vendors.manage_modal.manual_reason')}
            fieldId="vendors-manage-manual-reason"
            hint={t('vendors.manage_modal.manual_reason_hint')}
            required
          >
            <Input
              id="vendors-manage-manual-reason"
              value={manualReason}
              onChange={(event) => {
                setManualReason(event.target.value);
                setManualError(undefined);
              }}
              data-testid="vendors-manage-manual-reason"
              disabled={registerModelMut.isPending}
              required
            />
          </FormField>

          {manualError !== undefined ? (
            <p
              className="m-0 rounded border border-bad-line bg-bad-dim px-3 py-2 text-dense text-bad-bright"
              data-testid="vendors-manage-manual-error"
              role="alert"
            >
              {manualError}
            </p>
          ) : null}

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="secondary"
              loading={registerModelMut.isPending}
              disabled={updateMut.isPending || setKeyMut.isPending}
              data-testid="vendors-manage-manual-submit"
            >
              {registerModelMut.isPending
                ? t('vendors.manage_modal.manual_registering')
                : t('vendors.manage_modal.manual_submit')}
            </Button>
          </div>
        </form>

        <div className="flex justify-end border-t border-line-soft pt-4">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t('vendors.manage_modal.cancel')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
